import { useCallback } from 'react';
import { Button, type ButtonProps } from '@chakra-ui/react';
import { Crown } from 'lucide-react';
import type { UpgradePromptContext } from '@/types/subscription';
import { useOpenUpgradeModal } from '@/hooks/useOpenUpgradeModal';
import { FOCUS_RING_SX, INTERACTIVE_TRANSITION_SX, PRESS_FEEDBACK } from './lockedFeatureStyles';

export const DEFAULT_UPGRADE_CTA_LABEL = 'Upgrade to Paire+';

/** How an upgrade tap is carried out — shared by `LockedFeature` and `UpgradeCtaButton`. */
export interface UpgradeActionOptions {
  /** Which upgrade prompt to open. */
  context: UpgradePromptContext;
  /**
   * FULL replacement for the built-in `openUpgradeModal(context)` dispatch —
   * when provided, the built-in dispatch does NOT happen. Use it when the
   * caller must own the whole action (and in tests). For a side effect that
   * should merely accompany the normal dispatch, use `onBeforeUpgrade`.
   */
  onUpgradeClick?: () => void;
  /**
   * Runs BEFORE the dispatch (or before `onUpgradeClick`, if given) — for
   * things like a haptic tap or closing the modal the CTA sits in. May be
   * async: the dispatch waits for it. A failure here (e.g. haptics
   * unavailable) never blocks the upgrade path.
   */
  onBeforeUpgrade?: () => void | Promise<void>;
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return typeof (value as { then?: unknown } | null | undefined)?.then === 'function';
}

/**
 * Builds the upgrade tap handler: `onBeforeUpgrade` first, then either the
 * `onUpgradeClick` override or the built-in `openUpgradeModal(context)`
 * dispatch. A synchronous `onBeforeUpgrade` keeps the whole chain
 * synchronous (no extra microtask before the dispatch).
 */
export function useUpgradeAction({ context, onUpgradeClick, onBeforeUpgrade }: UpgradeActionOptions): () => void {
  const openUpgradeModal = useOpenUpgradeModal();

  return useCallback(() => {
    const proceed = () => {
      if (onUpgradeClick) {
        onUpgradeClick();
      } else {
        openUpgradeModal(context);
      }
    };

    let before: void | Promise<void>;
    try {
      before = onBeforeUpgrade?.();
    } catch {
      // A failed pre-step must never make the upgrade CTA dead.
      before = undefined;
    }

    if (isPromiseLike(before)) {
      // Either outcome proceeds, for the same reason.
      void before.then(proceed, proceed);
    } else {
      proceed();
    }
  }, [context, onUpgradeClick, onBeforeUpgrade, openUpgradeModal]);
}

export interface UpgradeCtaButtonProps extends UpgradeActionOptions {
  /** Defaults to "Upgrade to Paire+". */
  label?: string;
  /** `sm` for compact placements (still a 44px touch target). Defaults to `md`. */
  size?: 'sm' | 'md';
  /** Layout hooks for the host (e.g. `flex={1}` in a modal footer). Chrome is not overridable. */
  layout?: Pick<ButtonProps, 'flex' | 'w' | 'alignSelf'>;
  /** Stable test/automation hook. */
  testId?: string;
  /**
   * The id of the element that says what this CTA is for (e.g. a card's
   * headline), exposed as `aria-describedby`. The accessible NAME stays the
   * visible label: never replace it with an `aria-label` that omits that text.
   */
  describedBy?: string;
}

/**
 * The single "Upgrade to Paire+" call to action: a Crown glyph (always Crown
 * — the CTA says what Paire+ buys; `LockedFeature`'s `icon` override only
 * affects the surface's own chip) and a label, dispatching through
 * `useOpenUpgradeModal`. Exported so modal footers that keep a "Maybe Later"
 * action beside it (`CreateListModal`, `CreateCollaborativeListModal`) can
 * converge on the same button without a second CTA on the banner above.
 *
 * Colours are explicit tokens, never `colorScheme="purple"`: Chakra flips
 * that scheme to purple.200 with dark text in dark mode, which would make
 * this button a different control per mode. `--paire-plus-pill-text` on
 * `--color-primary-purple` is 5.70:1 (7.06:1 on hover) in both modes.
 *
 * Every CTA that opens the upgrade dialog (any that does not replace the action
 * with `onUpgradeClick`) carries `aria-haspopup="dialog"`.
 */
export function UpgradeCtaButton({
  label,
  size = 'md',
  layout,
  testId,
  describedBy,
  ...action
}: UpgradeCtaButtonProps) {
  const onUpgrade = useUpgradeAction(action);
  // The built-in dispatch opens the upgrade dialog; a caller that replaces the whole action
  // (`onUpgradeClick`) owns what happens, so nothing is promised on its behalf.
  const opensDialog = !action.onUpgradeClick;

  return (
    <Button
      type="button"
      size={size}
      minH="44px"
      bg="var(--color-primary-purple)"
      color="var(--paire-plus-pill-text)"
      _hover={{ bg: 'var(--color-purple-600)' }}
      _active={{ bg: 'var(--color-purple-700)', ...PRESS_FEEDBACK }}
      leftIcon={<Crown size={size === 'sm' ? 14 : 16} aria-hidden="true" />}
      onClick={onUpgrade}
      aria-haspopup={opensDialog ? 'dialog' : undefined}
      aria-describedby={describedBy}
      data-testid={testId}
      sx={INTERACTIVE_TRANSITION_SX}
      {...FOCUS_RING_SX}
      {...layout}
    >
      {label ?? DEFAULT_UPGRADE_CTA_LABEL}
    </Button>
  );
}
